import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import useEvents from '../../hooks/useEvents';

const EventCard = ({ image, date, title, description, link, index }) => {
  const cardRef = useRef(null);
  
  useEffect(() => {
    if (!cardRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('show');
          }, index * 150);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(cardRef.current);
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);

  // Tarih formatlama
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return d.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div 
      ref={cardRef}
      className="event-card bg-white rounded-xl shadow-lg overflow-hidden transform opacity-0 translate-y-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
    >
      <div className="relative overflow-hidden aspect-w-16 aspect-h-9">
        <img 
          src={image || '/api/placeholder/400/250'} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
          onError={(e) => {
            e.target.src = '/api/placeholder/400/250';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
          <div className="p-6">
            <div className="inline-block px-3 py-1 bg-indigo-600 text-white text-sm font-medium rounded-full mb-2">
              {formatDate(date)}
            </div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <p className="text-gray-600 mb-6">{description}</p>
        <Link to={link}>
          <Button 
            variant="outline" 
            size="sm"
            className="rounded-full border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors duration-300"
          >
            Detaylar
          </Button>
        </Link>
      </div>
    </div>
  );
};

const HomeEvents = () => {
  const sectionRef = useRef(null);
  const { getUpcomingEvents, loading } = useEvents();
  
  // Get upcoming 3 events
  const events = getUpcomingEvents(3);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelector('.section-title').classList.add('show');
          entry.target.querySelector('.section-description').classList.add('show');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(sectionRef.current);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 relative">
      {/* Dekoratif arka plan elementleri */}
      <div className="absolute right-0 top-0 w-1/3 h-1/3 bg-indigo-50 rounded-bl-full opacity-50"></div>
      <div className="absolute left-0 bottom-0 w-1/4 h-1/4 bg-purple-50 rounded-tr-full opacity-70"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-6 opacity-0 transform translate-y-8 transition-all duration-700">
            Yaklaşan Etkinlikler
          </h2>
          <p className="section-description text-xl text-gray-600 max-w-3xl mx-auto opacity-0 transform translate-y-8 transition-all duration-700 delay-200">
            Topluluğumuzun düzenlediği etkinliklere katılarak müzik dolu anlar yaşayın.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Etkinlikler yükleniyor...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-700 mb-2">Yaklaşan etkinlik bulunmuyor</h3>
            <p className="text-gray-500">Yeni etkinlikler için takipte kalın!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event, index) => (
              <EventCard
                key={event.id}
                image={event.image}
                date={event.date}
                title={event.title}
                description={event.description}
                link={`/events/${event.id}`}
                index={index}
              />
            ))}
          </div>
        )}

        <div className="text-center mt-12 opacity-0 transform translate-y-8 transition-all duration-700 delay-700 view-all-btn">
          <Link to="/events">
            <Button 
              variant="outline"
              className="px-8 py-3 rounded-full border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors duration-300"
            >
              Tüm Etkinlikler
            </Button>
          </Link>
        </div>
      </div>
      
      {/* CSS Animasyonları */}
      <style jsx>{`
        .event-card.show {
          opacity: 1;
          transform: translateY(0);
        }
        
        .section-title.show,
        .section-description.show,
        .view-all-btn.show {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
      
      {/* View All button animation */}
      <script jsx>{`
        document.addEventListener('DOMContentLoaded', () => {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                entry.target.classList.add('show');
                observer.unobserve(entry.target);
              }
            });
          }, { threshold: 0.5 });
          
          const viewAllBtn = document.querySelector('.view-all-btn');
          if (viewAllBtn) observer.observe(viewAllBtn);
        });
      `}</script>
    </section>
  );
};

export default HomeEvents;